import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const roles = [
  {
    title: "Product managers",
    line: "Helping product managers become better product managers at work.",
    detail:
      "Roadmaps, stakeholder updates, and trade-off calls stay clear even when your calendar is back to back.",
    testId: "domain-biology",
  },
  {
    title: "Sales people",
    line: "Helping sales people get more of their time back.",
    detail:
      "Follow-ups, proposals, and check-ins go out in your voice instead of sitting in drafts until late evening.",
    testId: "domain-nutrition",
  },
  {
    title: "People managers",
    line: "Helping people managers stay available to their teams.",
    detail:
      "Your team gets timely answers and direction without you living in Slack and email all day.",
    testId: "domain-physiology",
  },
  {
    title: "Engineering managers",
    line: "Helping engineering managers keep context with the team.",
    detail:
      "Priorities, technical calls, and status updates travel with the team when you are in deep work or another timezone.",
    testId: "domain-mobility",
  },
  {
    title: "Founders and operators",
    line: "Helping founders stay on top of what actually matters.",
    detail:
      "Investor notes, hiring threads, and daily fires do not pile up while you are building the product.",
    testId: "domain-founders",
  },
  {
    title: "Consultants and freelancers",
    line: "Helping consultants show up prepared for every client.",
    detail:
      "Context from past projects and open action items are ready before you join the call.",
    testId: "domain-consultants",
  },
  {
    title: "Customer success",
    line: "Helping customer success managers respond without sounding scripted.",
    detail:
      "Account history and tone stay intact so customers hear you, not a pasted template.",
    testId: "domain-cs",
  },
  {
    title: "Busy professionals at home",
    line: "Helping you keep personal life from falling behind work.",
    detail:
      "School forms, appointments, family logistics, and reminders get handled in the same place you run your work week.",
    testId: "domain-personal",
  },
];

function RoleCard({ role, index, isInView }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: 0.1 + index * 0.08 }}
      className="group"
      data-testid={role.testId}
    >
      <div className="h-full border border-pragti-border rounded-lg p-8 sm:p-9 bg-pragti-bg hover:-translate-y-1 hover:shadow-lg hover:border-pragti-text-secondary/30 transition-all duration-300">
        <h3 className="font-heading text-xl tracking-tight text-pragti-text mb-3">
          {role.title}
        </h3>
        <p className="font-body text-base text-pragti-text mb-3 leading-snug">{role.line}</p>
        <p className="font-body text-sm text-pragti-text-secondary leading-relaxed">
          {role.detail}
        </p>
      </div>
    </motion.article>
  );
}

export default function Roles() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      id="roles"
      data-testid="domains-section"
      ref={ref}
      className="relative py-24 sm:py-32 lg:py-40 px-6 sm:px-8 scroll-mt-24"
    >
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mb-16 lg:mb-20">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="font-mono text-xs tracking-[0.2em] uppercase text-pragti-accent mb-6"
            data-testid="domains-overline"
          >
            Who it helps
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-pragti-text"
            data-testid="domains-heading"
          >
            Same role.
            <br />
            <span className="text-pragti-text-secondary font-light">
              More done, with less running in your head.
            </span>
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {roles.map((role, index) => (
            <RoleCard key={role.title} role={role} index={index} isInView={isInView} />
          ))}
        </div>
      </div>
    </section>
  );
}
